import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Org, useOrg } from '../../org';
import { logger } from '../../../utils/logger';
import { useUser } from '../../authentication';
import { getOrg } from '../../org';
import { getAssociatedChurchAccounts } from '../api/getAssociatedChurchAccounts';
import { linkChurchAccount } from '../api/linkChurchAccount';
import { ChurchAccount } from '../models/churchAccount';
import { sortAssociatedChurchAccounts } from '../utils/sortAssociatedChurchAccounts';

type PartialOrg = Pick<Org, 'id' | 'name' | 'churchAccountId'>;

type UseConnectChurchAccountModalResult = {
  state: ModalState;
  submissionState: SubmissionState;
  onSelectChurchAccount: (account: ChurchAccount) => void;
  onConnectChurchAccount: () => Promise<boolean>;
};

type ModalState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; org: PartialOrg; churchAccounts: ChurchAccount[]; selectedChurchAccount?: ChurchAccount };

const useModalState = () => {
  const [state, setState] = useState<ModalState>({ status: 'loading' });
  const { updateOrg: updateOrgContext } = useOrg();

  const updateOrg = (churchAccountName: string, churchAccountId: string) => {
    updateOrgContext(prev => ({
      ...prev,
      name: churchAccountName,
      churchAccountId,
    }));

    setState(prev =>
      prev.status === 'ready' ? { ...prev, org: { ...prev.org, name: churchAccountName, churchAccountId } } : prev
    );
  };

  const actions = {
    updateOrg,
    setSelectedChurchAccount: (selectedChurchAccount?: ChurchAccount) =>
      setState(prev => (prev.status === 'ready' ? { ...prev, selectedChurchAccount } : prev)),
    setReady: (org: Org, churchAccounts: ChurchAccount[], selectedChurchAccount?: ChurchAccount) =>
      setState({ status: 'ready', org, churchAccounts, selectedChurchAccount }),
    setError: () => setState({ status: 'error' }),
  };

  return [state, actions] as const;
};

type SubmissionState =
  | { status: 'idle' }
  | { status: 'error'; code: string; message: string; disableButton: boolean }
  | { status: 'submitting' }
  | { status: 'success' };

const useSubmissionState = () => {
  const [state, setState] = useState<SubmissionState>({ status: 'idle' });

  const getErrorDataFromCode = (errorCode: string) => {
    switch (errorCode) {
      case 'AlreadyLinked':
      case 'ChurchAccountAlreadyLinkedToAnotherOrg':
        return {
          message: 'This church account has already been connected',
          disableButton: true,
        };
      case 'Forbidden':
        return {
          message: 'Additional permission required to connect this church account',
          disableButton: true,
        };
      default:
        return {
          message: 'An error occurred. Please try again.',
          disableButton: false,
        };
    }
  };

  const actions = {
    setIdle: () => setState({ status: 'idle' }),
    setSubmitting: () => setState({ status: 'submitting' }),
    setSuccess: () => setState({ status: 'success' }),
    setError: (errorCode: string) => setState({ status: 'error', code: errorCode, ...getErrorDataFromCode(errorCode) }),
  };

  return [state, actions] as const;
};

export const useConnectChurchAccountModal = (preSelectedAccountId?: string): UseConnectChurchAccountModalResult => {
  const { user } = useUser();
  const [state, { updateOrg, setSelectedChurchAccount, setReady, setError }] = useModalState();
  const [submissionState, submissionActions] = useSubmissionState();

  const onConnectChurchAccount = () => {
    if (state.status !== 'ready' || !state.selectedChurchAccount || submissionState.status === 'submitting')
      return Promise.resolve(false);

    const { org, selectedChurchAccount } = state;

    submissionActions.setSubmitting();

    return linkChurchAccount(org.id, selectedChurchAccount.churchAccountId, selectedChurchAccount.name)
      .then(() => {
        updateOrg(selectedChurchAccount.name, selectedChurchAccount.churchAccountId);
        submissionActions.setSuccess();
        return true;
      })
      .catch(error => {
        logger.error('Failed to connect church account', error);
        submissionActions.setError((error as AxiosError).code || '');
        return false;
      });
  };

  const onSelectChurchAccount = (account: ChurchAccount) => {
    const hasConnectPermission =
      account.permissions?.some(p => p.name === 'linkToOrganization' && p.hasAccess) ?? false;

    if (account.orgId) {
      submissionActions.setError('AlreadyLinked');
    } else if (!hasConnectPermission) {
      submissionActions.setError('Forbidden');
    } else {
      submissionActions.setIdle();
    }

    setSelectedChurchAccount(account);
  };

  useEffect(() => {
    if (!user?.lastSelectedAccount) {
      logger.error('Invalid user information');
      return setError();
    }

    Promise.all([getOrg(user.lastSelectedAccount), getAssociatedChurchAccounts()])
      .then(([orgResponse, associatedChurchAccounts]) => {
        if (!orgResponse) return setError();
        const org = { ...orgResponse, id: orgResponse._id.id } as PartialOrg;

        if (associatedChurchAccounts.length === 0) {
          logger.info('User has no associated church accounts');
          return setReady(org, [], undefined);
        }

        const sortedAssociatedChurchAccounts = sortAssociatedChurchAccounts(associatedChurchAccounts);

        const initialSelection =
          (preSelectedAccountId &&
            sortedAssociatedChurchAccounts.find(a => a.churchAccountId === preSelectedAccountId)) ||
          sortedAssociatedChurchAccounts[0];

        setReady(org, sortedAssociatedChurchAccounts, initialSelection);
        onSelectChurchAccount(initialSelection);
      })
      .catch(error => {
        logger.error('Failed to load associated church accounts', error);
        setError();
      });
  }, []);

  return {
    state,
    submissionState,
    onSelectChurchAccount,
    onConnectChurchAccount,
  };
};
